import React, { createRef, RefObject, useContext, useEffect, useMemo, useState } from "react";
import {
	cn,
	f2u,
	useUniqueId,
	DataObserveKey,
	VisualComponent,
	Divider,
	Icon,
	InlineText,
	Tooltip,
	SrOnly,
	Link,
	IconChevron,
} from "@siteimprove/fancylib";
import { BaseInteractiveList } from "../../actions-and-controls/base-interactive-list/base-interactive-list";
import { DropdownSectionHeader } from "../../actions-and-controls/dropdown-section-header/dropdown-section-header";
import { assignKnobName } from "../../../utils/documentation-utils";
import { useSearchableList } from "../../../utils/hooks";
import { useLabTranslations } from "../../../translations/translations";
import { useDesignToken } from "../../context/theme/theme";
import { InputField } from "../../forms-and-inputs/input-field/input-field";
import { TextHighlight } from "../../text/text-highlight/text-highlight";
import { PageChanger } from "../../navigation/pagination/page-changer";
import {
	ListPageChangerProps,
	useListPageChanger,
	useListPageChangeSelection,
} from "./page-changer-hooks";
import * as scss from "./action-list.scss";

export type ActionListProps = ActionListValues &
	ActionListContextValues &
	DataObserveKey &
	VisualComponent;

export type ActionListValues = {
	/** Id of the element wrapping the list */
	id?: string;
	/** Label to describe the list */
	"aria-label"?: string;
	/** alternative to providing an aria-label */
	"aria-labelledby"?: string;
	/** List of strings or objects to display in the list */
	items: ActionListItem[];
	/** Custom render function for items in the list */
	itemRenderer?: (renderProps: ItemRendererProps<ListOptionItemWithRefs>) => JSX.Element;
	/** Aria role the list */
	role?: string;
	/** Aria role of the items in the list */
	optionRole?: string;
	/** Enables item search functionality */
	searchable?: boolean;
	/** Whether to close the popover or accordion on item select */
	closeOnSelect?: boolean;
	/** If list needs to be paginated */
	pageChanger?: ListPageChangerProps;
};

export type ListOptionItem = ActionItem | LinkItem | CustomItem | GroupedItem;
export type ActionListItem = ListOptionItem | Divider | SectionHeader;

type SharedProps = {
	text: string;
	description?: string;
	icon?: JSX.Element;
	iconFill?: string;
	render?: (props: Omit<SharedProps, "text"> & { text: JSX.Element }) => JSX.Element;
	noStyling?: boolean;
	selected?: boolean;
	disabled?: boolean;
	tooltip?: string;
} & DataObserveKey;

export type ActionItem = { onClick: () => void } & SharedProps;
export type LinkItem = { href: string; openNew?: boolean } & SharedProps;
export type CustomItem = {
	render: (props: Omit<SharedProps, "text"> & { text: JSX.Element }) => JSX.Element;
} & SharedProps;
export type GroupedItem = {
	onClick?: () => void;
	children: ActionListItem[];
	expanded?: boolean;
} & SharedProps;

type Divider = { isDivider: true };

type SectionHeader = Omit<SharedProps, "disabled"> & { isSectionHeader: true }; // You can't really "disable" a section header

type withIndex<T> = T & { index: number };
type withParent<T> = T & { parent: number };

type GroupedItemWithRefs = withIndex<GroupedItem> & {
	children: withParent<ActionListItemWithGroupRefs>[];
	groupRef: RefObject<HTMLLIElement>;
	childrenWrapperRef: RefObject<HTMLUListElement>;
};
type ListOptionItemWithRefs = ActionItem | LinkItem | CustomItem | GroupedItemWithRefs;
type ActionListItemWithGroupRefs = withIndex<ListOptionItemWithRefs | Divider | SectionHeader>;

type ActionListContextValues = {
	id?: string;
	listClassName?: string;
	/**
	 * Should be assigned to the first focusable element in the popover.
	 *  This element will receive focus when the dropdown opens
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	firstFocusableRef?: React.RefObject<any>;
	openState?: { isOpen: boolean; setIsOpen: (isOpen: boolean) => void; close: () => void };
};

export const ActionListContext = React.createContext(null as ActionListContextValues | null);

function useUnwrapNullCtx<T>(ctx: React.Context<T | null>): T {
	const c = useContext(ctx);
	if (c === null) {
		return {} as T;
	}
	return c;
}

export const useActionListContext = (): ActionListContextValues =>
	useUnwrapNullCtx(ActionListContext);

function navigateTo(url: string, options: { openNew?: boolean }) {
	const { openNew = false } = options;

	if (openNew) {
		window.open(url, "_blank", "noopener");
	} else {
		location.href = url;
	}
}

function InternalActionList(props: ActionListProps) {
	const {
		id: _id,
		listClassName: _listClassName,
		firstFocusableRef: _firstFocusableRef,
		openState: _openState,
	} = useActionListContext();

	const uniqueId = useUniqueId("action-list");

	const {
		["aria-label"]: ariaLabel,
		["aria-labelledby"]: ariaLabelledby,
		itemRenderer,
		searchable,
		closeOnSelect,
		pageChanger,
		id = _id ?? uniqueId,
		listClassName = _listClassName,
		role = "menu",
		optionRole = "menuitem",
		firstFocusableRef = _firstFocusableRef,
		openState = _openState,
		className,
		...rest
	} = props;

	checkFirstAndLastItem(props.items);

	const initItems = useMemo(() => initActionListItems(props.items), [props.items]);
	const flatInitItems = useMemo(() => flatMapItems(initItems), [initItems]);

	const { expandedGroups, setExpandedGroups, updateExpandedGroups, toggleExpandedGroups } =
		useExpandedGroups(flatInitItems);

	const [query, setQuery] = React.useState("");
	const searchItems = useSearchableList<ActionListItemWithGroupRefs>(
		flatInitItems,
		query,
		stringifyItem
	);

	const items = useMemo(() => {
		if (query === "") return initItems;
		const idxs = searchItems.map((item) => item.index);
		return filterByIndex(initItems, idxs, setExpandedGroups);
	}, [searchItems]);

	const flatItems = useMemo(() => flatMapItems(items), [items]);

	const { page, pageChangeHandler, pageSize, paginatedItems } = useListPageChanger(
		items,
		pageChanger
	);

	const { selected, setSelected } = useListPageChangeSelection(
		flatItems,
		pageChangeHandler,
		pageSize
	);

	useEffect(() => {
		if (selected > -1) {
			const selectedItem = flatItems.find((item) => item.index === selected) as
				| withParent<ActionListItemWithGroupRefs>
				| undefined;
			const parentItem =
				selectedItem &&
				selectedItem.parent !== undefined &&
				flatItems.find((i) => i.index === selectedItem.parent);
			parentItem && updateExpandedGroups(parentItem as withIndex<GroupedItem>);
		}
	}, [selected]);

	const groupedItems = useMemo(
		() => items.filter((item) => isGroupedItem(item)) as GroupedItemWithRefs[],
		[items]
	);
	useGroupItems(groupedItems, expandedGroups, [page]);

	const shownItems = setSelectedInItems(paginatedItems, selected);

	const noIcons = shownItems.filter((item) => !("isDivider" in item) && item.icon).length === 0;

	const i18nLab = useLabTranslations();
	const { ColorTextInteractiveDarkDefault } = useDesignToken();
	const skipListId = useUniqueId("skipList");

	return (
		<div className={cn(scss.wrapper, className)} {...rest}>
			{searchable && (
				<InputField
					aria-autocomplete="list"
					aria-controls={id}
					aria-expanded={true}
					role="combobox"
					aria-label={i18nLab.search}
					autoFocus
					className={cn(scss.searchInputField)}
					onChange={setQuery}
					placeholder={i18nLab.search}
					type="search"
					ref={firstFocusableRef}
					value={query}
				/>
			)}
			<SrOnly>
				<Link href={`#${skipListId}`}>{i18nLab.skipList}</Link>
			</SrOnly>
			<BaseInteractiveList
				listId={id}
				selectedIndex={
					flatMapItems(shownItems).find((item) => "selected" in item && item.selected)?.index
				}
				listRef={searchable ? undefined : firstFocusableRef}
				listClassName={listClassName}
				liClassName={(item) => {
					if ("isDivider" in item) return scss.divider;
				}}
				items={shownItems}
				itemRenderer={(item) =>
					actionItemRenderer(
						{
							item,
							noIcons,
							needle: query,
							iconFill: ColorTextInteractiveDarkDefault,
							expandedGroups,
						},
						itemRenderer
					)
				}
				role={role}
				optionRole={optionRole}
				aria-label={ariaLabel ?? (searchable ? i18nLab.search : i18nLab.interactiveList)}
				aria-labelledby={ariaLabelledby}
				onItemSelect={(item) => {
					if (!isListOptionItem(item) || item.disabled) return;
					setSelected(item.index);
					if (isGroupedItem(item)) toggleExpandedGroups(item);
					clickOrNavigateTo(item);
					closeOnSelect && openState && openState.setIsOpen(false);
				}}
				isNotAnOption={(item) =>
					!isListOptionItem(item) || !hasExpandedParent(expandedGroups, item)
				}
				isDisabled={(item) => "disabled" in item && !!item.disabled}
				stringify={stringifyItem}
			/>
			{pageChanger && !(page == 1 && pageSize >= items.length) && (
				<div role="group" aria-label={i18nLab.listPaginationLabel}>
					<PageChanger
						{...Object.fromEntries(
							Object.entries(pageChanger).filter((e) => e[0] != "onPageChange")
						)}
						className={scss.pagination}
						total={items.length}
						page={page}
						setPage={pageChangeHandler}
					/>
				</div>
			)}
			<div id={skipListId} />
		</div>
	);
}

export type ItemRendererProps<T> = {
	item: T;
	noIcons: boolean;
	needle: string;
	iconFill: string;
	expandedGroups: number[];
};

export const actionItemRenderer = (
	renderProps: ItemRendererProps<ActionListItemWithGroupRefs>,
	customRenderer?: (renderProps: ItemRendererProps<ListOptionItemWithRefs>) => JSX.Element
): JSX.Element => {
	const { item, noIcons, needle, iconFill, expandedGroups } = renderProps;

	if ("isDivider" in item) {
		return <Divider />;
	}

	const maybeIcon = !noIcons && item.icon && (
		<span className={scss.icon}>
			<Icon size="medium" fill={item.iconFill ?? iconFill}>
				{item.icon}
			</Icon>
		</span>
	);

	if ("isSectionHeader" in item) {
		return <DropdownSectionHeader text={item.text} beforeText={maybeIcon} />;
	}

	if (customRenderer) {
		return customRenderer({ item, noIcons, needle, iconFill, expandedGroups });
	}

	const needleText = <TextHighlight value={item.text} needle={needle} caseSensitive={false} />;

	const actionContent = item.noStyling ? (
		needleText
	) : (
		<div className={cn(scss.actionMainContent)}>
			<div className={cn(scss.titleContent)}>
				{maybeIcon}
				<InlineText lineHeight="multi-line" tone={item.disabled ? "subtle" : "neutralDark"}>
					{needleText} {"href" in item && item.openNew && <span>&nbsp;&#8599;</span>}
				</InlineText>
			</div>
			<div>
				{item.description && (
					<InlineText size="xSmall" tone="subtle" lineHeight="multi-line">
						{item.description}
					</InlineText>
				)}
			</div>
		</div>
	);

	const renderAction = () => {
		if (isGroupedItem(item)) {
			const isExpanded = expandedGroups.includes(item.index);
			return (
				<button
					className={
						item.noStyling
							? scss.noStyling
							: cn(
									scss.action,
									scss.groupHeader,
									scss.button,
									item.selected && scss.selected,
									item.disabled && scss.disabled
							  )
					}
					data-observe-key={item["data-observe-key"]}
					disabled={item.disabled}
					tabIndex={-1}
					type="button"
				>
					{item.render?.({ ...item, text: needleText }) ?? actionContent}
					{!item.noStyling && <Icon rotation={isExpanded ? "180" : "0"}>{<IconChevron />}</Icon>}
				</button>
			);
		}

		if ("href" in item) {
			return (
				<a
					className={
						item.noStyling
							? scss.noStyling
							: cn(
									scss.action,
									scss.link,
									item.selected && scss.selected,
									item.disabled && scss.disabled
							  )
					}
					data-observe-key={item["data-observe-key"]}
					href={item.href}
					onClick={(e) => e.preventDefault()}
					rel={f2u(item.openNew && "noopener")}
					tabIndex={-1}
					target={f2u(item.openNew && "_blank")}
				>
					{item.render?.({ ...item, text: needleText }) ?? actionContent}
				</a>
			);
		}

		if ("onClick" in item) {
			return (
				<button
					className={
						item.noStyling
							? scss.noStyling
							: cn(
									scss.action,
									scss.button,
									item.selected && scss.selected,
									item.disabled && scss.disabled
							  )
					}
					data-observe-key={item["data-observe-key"]}
					disabled={item.disabled}
					tabIndex={-1}
					type="button"
				>
					{item.render?.({ ...item, text: needleText }) ?? actionContent}
				</button>
			);
		}

		return item.noStyling ? (
			item.render({ ...item, text: needleText })
		) : (
			<div
				className={cn(scss.action, item.selected && scss.selected, item.disabled && scss.disabled)}
				data-observe-key={item["data-observe-key"]}
			>
				{item.render({ ...item, text: needleText })}
			</div>
		);
	};

	return (
		<>
			{item.tooltip ? (
				<Tooltip
					variant={{ type: "interactive" }}
					content={item.tooltip}
					className={cn(scss.tooltip)}
				>
					{renderAction()}
				</Tooltip>
			) : (
				renderAction()
			)}
		</>
	);
};

assignKnobName(InternalActionList);
InternalActionList.displayName = "ActionList";

export function isGroupedItem(item: ActionListItem): item is GroupedItem {
	return "children" in item && Array.isArray(item.children);
}

export function isItemDivider(item: ActionListItem): boolean {
	return typeof item === "object" && "isDivider" in item;
}

export const divider = { isDivider: true } as const;

export const sectionHeader = (text: string, icon?: JSX.Element) =>
	({ text, icon, isSectionHeader: true } as const);

export function isListOptionItem(x: ActionListItem): x is ListOptionItem {
	return !("isDivider" in x || "isSectionHeader" in x);
}

export function hasExpandedParent(expanded: number[], item: ActionListItem): boolean {
	const i = item as withParent<ActionListItem>;
	if (i.parent !== undefined) {
		const parent = i.parent;
		return expanded.includes(parent);
	}
	return true;
}

export function stringifyItem(item: ActionListItem): string {
	if (isListOptionItem(item)) return item.text;
	return "";
}

function initActionListItems(
	items: ActionListItem[],
	indexCounter: { value: number } = { value: 0 },
	parentIndex?: number
): ActionListItemWithGroupRefs[] {
	// Add dividers before headers. We do this, so the dividers are in their own <li> elements,
	// which semantically makes more sense and makes padding automatically consistent.
	// 	`i` starts at `1`, so we don't add a divider above a header if it's the very first item.
	for (let i = 1; i < items.length; i++) {
		if ("isSectionHeader" in items[i]) {
			items.splice(i, 0, {
				isDivider: true,
			});
			i++; // <-- everything shifted, so we skip `i` forward to catch up.
		}
	}

	return items.map((item) => {
		const index = indexCounter.value;
		indexCounter.value++;
		if (isGroupedItem(item)) {
			return {
				...item,
				index,
				parent: parentIndex,
				groupRef: createRef<HTMLLIElement>(),
				childrenWrapperRef: createRef<HTMLUListElement>(),
				children: initActionListItems(
					item.children,
					indexCounter,
					index
				) as withParent<ActionListItemWithGroupRefs>[],
			};
		} else {
			return { ...item, index, parent: parentIndex };
		}
	});
}

function flatMapItems(
	items: ActionListItemWithGroupRefs[],
	flatItems: ActionListItemWithGroupRefs[] = []
): ActionListItemWithGroupRefs[] {
	items.forEach((item) => {
		if (isGroupedItem(item)) {
			flatItems.push(item);
			flatMapItems(item.children, flatItems);
		} else {
			flatItems.push(item);
		}
	});
	return flatItems;
}

function checkFirstAndLastItem(items: ActionListItem[]) {
	if (items.length > 0 && (isItemDivider(items[0]) || isItemDivider(items[items.length - 1]))) {
		throw new Error("First or last item in an ActionList cannot be a Divider");
	}
}

function clickOrNavigateTo(item: ActionListItem) {
	if ("onClick" in item && item.onClick) {
		item.onClick();
	} else if ("href" in item) {
		navigateTo(item.href, { openNew: item.openNew });
	}
}

function useExpandedGroups(flatItems: ActionListItemWithGroupRefs[]) {
	const findExpanded = () =>
		flatItems.filter((i) => "expanded" in i && i.expanded).map((i) => i.index);
	const [expandedGroups, setExpandedGroups] = useState<number[]>(findExpanded());

	useEffect(() => {
		setExpandedGroups(findExpanded());
	}, [flatItems]);

	const updateExpandedGroups = (item: withIndex<GroupedItem>) => {
		!expandedGroups.includes(item.index) && setExpandedGroups((prev) => [...prev, item.index]);
	};
	const toggleExpandedGroups = (item: withIndex<GroupedItem>) => {
		expandedGroups.includes(item.index)
			? setExpandedGroups((prev) => prev.filter((i) => i !== item.index))
			: setExpandedGroups((prev) => [...prev, item.index]);
	};

	return { expandedGroups, setExpandedGroups, updateExpandedGroups, toggleExpandedGroups };
}

function useGroupItems(items: GroupedItemWithRefs[], expanded: number[], deps: unknown[] = []) {
	useEffect(() => {
		items.forEach((group) => {
			if (group.groupRef.current && group.childrenWrapperRef.current) {
				// IDs - CAN'T USE useUniqueId HOOK OUTSIDE OF A FUNCTION COMPONENT
				const headerId = crypto.randomUUID();
				const wrapperId = crypto.randomUUID();

				// GROUP
				const curGroup = group.groupRef.current;
				curGroup.classList.add(scss.groupedActions);

				// GROUP HEADER
				const groupHeader = curGroup.children[0]; // "HEADER" will be the first LI in the group
				groupHeader.setAttribute("id", headerId);
				groupHeader.setAttribute("aria-controls", wrapperId);

				// CHILDREN
				const curWrapper = group.childrenWrapperRef.current;
				curWrapper.setAttribute("aria-labelledby", headerId);
				curWrapper.setAttribute("id", wrapperId);
				curWrapper.classList.add(scss.groupChildren);

				toggleGroup(group, expanded);
			}
		});
	}, [items, ...deps]);

	useEffect(() => {
		items.forEach((group) => {
			toggleGroup(group, expanded);
		});
	}, [expanded]);

	function toggleGroup(group: withIndex<GroupedItemWithRefs>, expanded: number[]) {
		if (group.groupRef.current && group.childrenWrapperRef.current) {
			const curGroup = group.groupRef.current;
			const groupHeader = curGroup.children[0]; // "HEADER" will be the first LI in the group
			const curWrapper = group.childrenWrapperRef.current;
			if (expanded.includes(group.index)) {
				groupHeader.setAttribute("aria-expanded", "true");
				curWrapper.setAttribute("aria-hidden", "false");
				curWrapper.classList.remove(scss.hidden);
			} else {
				groupHeader.setAttribute("aria-expanded", "false");
				curWrapper.setAttribute("aria-hidden", "true");
				curWrapper.classList.add(scss.hidden);
			}
		}
	}
}

function filterByIndex(
	items: ActionListItemWithGroupRefs[],
	filterIndex: number[],
	setExpandedGroups: (groups: number[]) => void
) {
	const filteredItems = recursiveFilterByIndex(items, filterIndex);
	setExpandedGroups(filteredItems.filter((item) => isGroupedItem(item)).map((i) => i.index));
	return filteredItems;
}

function recursiveFilterByIndex(
	items: ActionListItemWithGroupRefs[],
	filterIndex: number[],
	indexCounter: { value: number } = { value: -1 },
	parentIndex?: number
): ActionListItemWithGroupRefs[] {
	return items.reduce((acc: ActionListItemWithGroupRefs[], item) => {
		const itemWithParent = { ...item, parent: parentIndex };
		if (isGroupedItem(item)) {
			if (filterIndex.includes(item.index)) {
				indexCounter.value++;
				const idx = indexCounter.value;
				const itemWithNewIndex = {
					...itemWithParent,
					index: idx,
					children: item.children.map((i) => {
						indexCounter.value++;
						return { ...i, index: indexCounter.value, parent: idx };
					}),
				};
				acc.push(itemWithNewIndex as ActionListItemWithGroupRefs);
			} else {
				indexCounter.value++;
				const idx = indexCounter.value;
				const c = recursiveFilterByIndex(item.children, filterIndex, indexCounter, idx);
				if (c.length === 0) {
					indexCounter.value--;
				} else {
					const itemWithNewIndex = {
						...itemWithParent,
						index: idx,
						children: c.map((c) => ({ ...c, parent: idx })),
					};
					acc.push(itemWithNewIndex);
				}
			}
		} else if (filterIndex.includes(item.index)) {
			indexCounter.value++;
			const itemWithNewIndex = { ...itemWithParent, index: indexCounter.value };
			acc.push(itemWithNewIndex);
		}
		return acc;
	}, []);
}

function setSelectedInItems(
	items: ActionListItemWithGroupRefs[],
	selected: number
): ActionListItemWithGroupRefs[] {
	return items.map((item) => {
		if (isGroupedItem(item)) {
			return {
				...item,
				selected: item.index === selected,
				children: setSelectedInItems(
					item.children,
					selected
				) as withParent<ActionListItemWithGroupRefs>[],
			};
		} else {
			return { ...item, selected: item.index === selected };
		}
	});
}

export const ActionList = Object.assign(InternalActionList, { divider, sectionHeader });
